import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, Link, useLocation } from 'react-router-dom';
import * as Y from 'yjs';
import { useTranslation } from 'react-i18next';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import { useEhxcelSync } from './hooks/useEhxcelSync';
import { useAuth } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import AddCollaboratorModal from './components/AddCollaboratorModal';
import { SpreadsheetWorkspace } from './components/SpreadsheetWorkspace';
import { documentService } from './services/document.service';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';

// --- Collaborative Document Title ---
const DocumentTitle = ({ ydoc, documentId, token, isReadOnlyUser }: { ydoc: Y.Doc, documentId: string, token: string, isReadOnlyUser: boolean }) => {
    const { t } = useTranslation();
    const meta = ydoc.getMap<string>('metadata');
    const [title, setTitle] = useState('');

    useEffect(() => {
        const updateTitle = () => setTitle(meta.get('title') || t('header.untitled'));
        updateTitle();
        
        meta.observe(updateTitle);
        return () => meta.unobserve(updateTitle);
    }, [meta, t]);

    // Debounce the SQL Database update so the Dashboard sees the name change
    useEffect(() => {
        if (!title || title === t('header.untitled') || !token) return;
        
        const timeout = setTimeout(async () => {
            try {
                const apiUrl = import.meta.env.VITE_API_URL || 'https://api.cellysheets.ca/api'; 
                await fetch(`${apiUrl}/documents/${documentId}`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ title })
                });
            } catch (err) {
                console.error("Failed to sync title to database:", err);
            }
        }, 1000);

        return () => clearTimeout(timeout);
    }, [title, documentId, token, t]);

    if (isReadOnlyUser) {
        return <span className="text-xl md:text-2xl font-bold tracking-tight text-white">{title || t('header.untitled')}</span>;
    }

    return (
        <input
            value={title}
            onChange={(e) => {
                setTitle(e.target.value);
                meta.set('title', e.target.value);
            }}
            className="bg-transparent text-xl md:text-2xl font-bold tracking-tight text-white placeholder-red-200 outline-none border-b border-transparent focus:border-white transition-colors min-w-[200px] md:min-w-[250px]"
            placeholder={t('header.untitled')}
        />
    );
};

const SpreadsheetContainer = () => {
    const { t } = useTranslation();
    const { token, isLoading } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { id } = useParams<{ id: string }>();
    const location = useLocation();

    const [access, setAccess] = useState<{ role: string, isPublic: boolean, title: string } | null>(null);
    const [accessError, setAccessError] = useState<string | null>(null);

    useEffect(() => {
        if (isLoading) return;

        const fetchAccess = async () => {
            setAccessError(null);
            try {
                const res = await documentService.getAccess(token || '', id!);
                setAccess(res);
            } catch (err: any) {
                setAccessError(err.message || 'You do not have access to this document.');
            }
        };
        fetchAccess();
    }, [id, token, isLoading]);

    const { doc, provider, synced } = useEhxcelSync(id!, token || '', access !== null && !accessError);

    if (isLoading) {
        return (
            <div className="h-screen w-screen flex items-center justify-center bg-gray-100">
                <div className="animate-pulse font-bold text-gray-500">Checking session...</div>
            </div>
        );
    }

    if (accessError) {
        return (
            <div className="h-screen w-screen flex flex-col items-center justify-center bg-gray-100 p-4">
                <h1 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h1>
                <p className="text-gray-600 mb-6">{accessError}</p>
                <Link to="/" className="px-4 py-2 bg-red-600 text-white font-bold rounded hover:bg-red-700">Return to Dashboard</Link>
            </div>
        );
    }

    if (!access) {
        return (
            <div className="h-screen w-screen flex items-center justify-center bg-gray-100">
                <div className="animate-pulse font-bold text-gray-500">Checking permissions...</div>
            </div>
        );
    }

    const isReadOnlyUser = access.role === 'VIEWER';

    return (
    <div className="h-screen w-screen bg-gray-100 flex flex-col overflow-hidden">
        <header className="w-full bg-red-700 text-white p-4 shadow-md flex items-center justify-between z-10 shrink-0">
            <div className="flex items-center space-x-2 md:space-x-4">
                {token ? (
                    <Link to="/" className="hover:text-red-200 font-medium flex items-center" title={t('nav.dashboard')}>
                        {/* Mobile Home Icon */}
                        <svg className="w-5 h-5 md:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path></svg>
                        {/* Desktop Text */}
                        <span className="hidden md:inline">← {t('nav.dashboard')}</span>
                    </Link>
                ) : (
                    <Link to="/register" className="bg-white text-red-700 px-3 py-1 rounded text-sm font-bold shadow-sm hover:bg-gray-100 transition-colors">
                        <span className="hidden md:inline">{t('header.tryCellySheets')}</span>
                        <span className="md:hidden">Try</span>
                    </Link>
                )}
                {doc ? (
                    <DocumentTitle ydoc={doc} documentId={id!} token={token || ''} isReadOnlyUser={isReadOnlyUser} />
                ) : (
                    <h1 className="text-xl md:text-2xl font-bold tracking-tight">{access.title || t('header.loading')}</h1>
                )}
            </div>
            
            <div className="flex items-center space-x-3">
                <div className="hidden md:block">
                    <LanguageSwitcher theme="dark"/>
                </div>
                {access.role !== 'VIEWER' && (
                    <button 
                        onClick={() => setIsModalOpen(true)}
                        className="hidden md:block bg-red-600 hover:bg-red-500 px-3 py-1 rounded text-sm font-medium transition cursor-pointer"
                    >
                        {t('header.share')}
                    </button>
                )}
                {/* <div className="text-sm font-medium bg-red-800 px-3 py-1 rounded-full hidden sm:block">
                    {synced ? t('header.synced') : t('header.connecting')}
                </div> */}
                {/* Mobile Sync Indicator */}
                {/* <div className="text-sm font-medium bg-red-800 w-8 h-8 rounded-full flex sm:hidden items-center justify-center">
                    {synced ? '🟢' : '🟡'}
                </div> */}
            </div>
        </header>

        <main className="flex-1 w-full flex flex-col overflow-hidden relative">
            {synced && doc && provider ? (
                <SpreadsheetWorkspace 
                    ydoc={doc} 
                    awareness={provider.awareness!} 
                    initialImport={location.state as any} 
                    onOpenShare={() => setIsModalOpen(true)}
                    isReadOnlyUser={isReadOnlyUser}
                />
            ) : (
                <div className="flex items-center justify-center h-full text-xl font-bold text-gray-400 animate-pulse">
                    Loading Spreadsheet Engine...
                </div>
            )}
        </main>

        {isModalOpen && id && (
            <AddCollaboratorModal documentId={id} onClose={() => setIsModalOpen(false)} />
        )}
    </div>
    );
};

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        
        {/* We move /d/:id OUT of ProtectedRoute so public users can access it */}
        <Route path="/d/:id" element={<SpreadsheetContainer />} />

        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/profile" element={<Profile />} />
        </Route>
        
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <PWAInstallPrompt />
    </BrowserRouter>
  );
}